/**
 * Local Usage Dashboard
 *
 * Electron-specific usage tracking dashboard showing:
 * - GPTZero word quota card (when configured)
 * - Summary cards (calls, tokens, cost, backend split)
 * - Usage by model table
 * - Recent calls list
 *
 * @module @humanizer/studio/components/settings/LocalUsageDashboard
 */

import { useState, useEffect, useCallback } from 'react';
import { useLocalAdmin } from '../../hooks/useLocalAdmin';

// ═══════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════

function formatNumber(n: number): string {
  return n.toLocaleString();
}

function formatCost(millicents: number): string {
  if (millicents === 0) return 'Free';
  const dollars = millicents / 100000;
  return dollars < 0.01 ? '< $0.01' : `$${dollars.toFixed(2)}`;
}

function formatLatency(ms: number | null): string {
  if (!ms) return '-';
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ═══════════════════════════════════════════════════════════════════
// GPTZERO QUOTA CARD
// ═══════════════════════════════════════════════════════════════════

interface GptzeroQuotaData {
  monthlyWordLimit: number;
  enabled: boolean;
  proxyEnabled: boolean;
  wordsUsed: number;
  callCount: number;
  billingMonth: string;
}

function GptzeroQuotaCard({
  data,
  source,
  onUpdate,
}: {
  data: GptzeroQuotaData;
  source: 'byok' | 'cloud' | 'none';
  onUpdate: (update: { monthlyWordLimit?: number; enabled?: boolean; proxyEnabled?: boolean }) => Promise<void>;
}) {
  const [showConfig, setShowConfig] = useState(false);
  const [wordLimit, setWordLimit] = useState(data.monthlyWordLimit);
  const [enabled, setEnabled] = useState(data.enabled);
  const [proxyEnabled, setProxyEnabled] = useState(data.proxyEnabled);
  const [saving, setSaving] = useState(false);

  const pct = data.monthlyWordLimit > 0
    ? Math.min(100, Math.round((data.wordsUsed / data.monthlyWordLimit) * 100))
    : 0;

  const fillClass = pct >= 90
    ? 'gptzero-quota__progress-fill--danger'
    : pct >= 70
      ? 'gptzero-quota__progress-fill--warning'
      : '';

  const badgeClass = !data.enabled
    ? 'gptzero-quota__badge--disabled'
    : source === 'byok'
      ? 'gptzero-quota__badge--byok'
      : 'gptzero-quota__badge--cloud';

  const badgeText = !data.enabled
    ? 'Disabled'
    : source === 'byok'
      ? 'BYOK'
      : source === 'cloud'
        ? 'Cloud'
        : 'Not configured';

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate({ monthlyWordLimit: wordLimit, enabled, proxyEnabled });
      setShowConfig(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="gptzero-quota">
      <div className="gptzero-quota__header">
        <h3 className="gptzero-quota__title">GPTZero Usage</h3>
        <span className={`gptzero-quota__badge ${badgeClass}`}>{badgeText}</span>
      </div>
      <div className="gptzero-quota__body">
        <div className="gptzero-quota__progress">
          <div className="gptzero-quota__progress-header">
            <span className="gptzero-quota__progress-label">
              {formatNumber(data.wordsUsed)} / {formatNumber(data.monthlyWordLimit)} words
            </span>
            <span className="gptzero-quota__progress-pct">{pct}% used this month</span>
          </div>
          <div className="gptzero-quota__progress-bar">
            <div
              className={`gptzero-quota__progress-fill ${fillClass}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        <div className="gptzero-quota__meta">
          <span className="gptzero-quota__meta-left">
            {formatNumber(data.callCount)} calls &middot; {data.billingMonth}
          </span>
          <button
            className="gptzero-quota__configure-btn"
            onClick={() => setShowConfig(!showConfig)}
            type="button"
          >
            {showConfig ? 'Close' : 'Configure'}
          </button>
        </div>

        {showConfig && (
          <div className="gptzero-quota__config">
            <div className="gptzero-quota__config-field">
              <label htmlFor="gptzero-word-limit">Monthly word limit</label>
              <input
                id="gptzero-word-limit"
                type="number"
                min={0}
                step={10000}
                value={wordLimit}
                onChange={(e) => setWordLimit(Number(e.target.value))}
              />
            </div>

            <div className="gptzero-quota__config-toggle">
              <div className="gptzero-quota__config-toggle-info">
                <span className="gptzero-quota__config-toggle-label">Enable GPTZero</span>
                <span className="gptzero-quota__config-toggle-desc">Master kill switch for all GPTZero calls</span>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
                <span className="settings-toggle__slider" />
              </label>
            </div>

            <div className="gptzero-quota__config-toggle">
              <div className="gptzero-quota__config-toggle-info">
                <span className="gptzero-quota__config-toggle-label">Cloud proxy</span>
                <span className="gptzero-quota__config-toggle-desc">Allow GPTZero calls through the cloud proxy (Pro+ only)</span>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={proxyEnabled}
                  onChange={(e) => setProxyEnabled(e.target.checked)}
                />
                <span className="settings-toggle__slider" />
              </label>
            </div>

            <div className="settings-form__actions">
              <button
                className="btn btn--sm btn--primary"
                onClick={handleSave}
                disabled={saving}
                type="button"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                className="btn btn--sm btn--ghost"
                onClick={() => {
                  setWordLimit(data.monthlyWordLimit);
                  setEnabled(data.enabled);
                  setProxyEnabled(data.proxyEnabled);
                  setShowConfig(false);
                }}
                type="button"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

export function LocalUsageDashboard() {
  const {
    getUsageSummary, getUsageByModel, getUsageByBackend, getRecentCalls,
    getGptzeroConfig, setGptzeroConfig,
  } = useLocalAdmin();

  const [summary, setSummary] = useState<any>(null);
  const [byModel, setByModel] = useState<any[]>([]);
  const [byBackend, setByBackend] = useState<any[]>([]);
  const [recentCalls, setRecentCalls] = useState<any[]>([]);
  const [gptzeroData, setGptzeroData] = useState<GptzeroQuotaData | null>(null);
  const [gptzeroSource, setGptzeroSource] = useState<'byok' | 'cloud' | 'none'>('none');
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, m, b, r, g] = await Promise.all([
        getUsageSummary(),
        getUsageByModel(),
        getUsageByBackend(),
        getRecentCalls(20),
        getGptzeroConfig(),
      ]);
      setSummary(s);
      setByModel(m || []);
      setByBackend(b || []);
      setRecentCalls(r || []);
      if (g) {
        setGptzeroData(g as GptzeroQuotaData);
        // Determine source from availability
        if (window.electronAnalyze?.gptzeroAvailability) {
          window.electronAnalyze.gptzeroAvailability().then((avail: any) => {
            setGptzeroSource(avail?.source || 'none');
          }).catch(() => {});
        }
      }
    } catch {
      // Non-fatal
    } finally {
      setLoading(false);
    }
  }, [getUsageSummary, getUsageByModel, getUsageByBackend, getRecentCalls, getGptzeroConfig]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleGptzeroUpdate = useCallback(async (update: { monthlyWordLimit?: number; enabled?: boolean; proxyEnabled?: boolean }) => {
    await setGptzeroConfig(update);
    // Refresh data after config change
    const g = await getGptzeroConfig();
    if (g) setGptzeroData(g as GptzeroQuotaData);
  }, [setGptzeroConfig, getGptzeroConfig]);

  if (loading) {
    return (
      <div className="settings-section">
        <div className="settings-loading">
          <span className="settings-loading__spinner" />
        </div>
      </div>
    );
  }

  const noData = !summary || summary.totalCalls === 0;

  return (
    <div className="settings-section">
      <div className="settings-section__header">
        <h2 className="settings-section__title">Local Usage</h2>
        <p className="settings-section__description">
          {summary?.billingPeriod
            ? `LLM calls this month (${summary.billingPeriod})`
            : 'Track your local LLM usage'}
        </p>
      </div>

      <div className="settings-section__content">
        {/* GPTZero Quota Card (shown when config exists) */}
        {gptzeroData && (
          <GptzeroQuotaCard
            data={gptzeroData}
            source={gptzeroSource}
            onUpdate={handleGptzeroUpdate}
          />
        )}

        {noData ? (
          <div className="settings-card">
            <p className="settings-empty-text">
              No usage data yet. Make an LLM call to start tracking.
            </p>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="usage-stats">
              <div className="usage-stat">
                <span className="usage-stat__value">{formatNumber(summary.totalCalls)}</span>
                <span className="usage-stat__label">Total Calls</span>
              </div>
              <div className="usage-stat">
                <span className="usage-stat__value">{formatNumber(summary.totalTokens)}</span>
                <span className="usage-stat__label">Total Tokens</span>
              </div>
              <div className="usage-stat">
                <span className="usage-stat__value">{formatCost(summary.totalCostMillicents)}</span>
                <span className="usage-stat__label">Estimated Cost</span>
              </div>
              <div className="usage-stat">
                <span className="usage-stat__value">{formatLatency(summary.avgLatencyMs)}</span>
                <span className="usage-stat__label">Avg Latency</span>
              </div>
            </div>

            {/* Backend Breakdown */}
            {byBackend.length > 0 && (
              <div className="settings-card">
                <h3 className="settings-card__title">By Backend</h3>
                <div className="settings-card__content">
                  <div className="usage-breakdown">
                    {byBackend.map((b: any) => (
                      <div key={b.backend} className="usage-breakdown__item">
                        <span className="usage-breakdown__label">
                          {b.backend === 'ollama' ? 'Local (Ollama)' :
                           b.backend === 'byok' ? 'BYOK (Your Key)' : 'Cloud'}
                        </span>
                        <span className="usage-breakdown__value">
                          {formatNumber(b.callCount)} calls &middot; {formatNumber(b.totalTokens)} tokens
                          {b.totalCostMillicents > 0 && ` · ${formatCost(b.totalCostMillicents)}`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* By Model */}
            {byModel.length > 0 && (
              <div className="settings-card">
                <h3 className="settings-card__title">By Model</h3>
                <div className="settings-card__content">
                  <table className="settings-table">
                    <thead>
                      <tr>
                        <th>Model</th>
                        <th>Calls</th>
                        <th>Tokens</th>
                        <th>Cost</th>
                        <th>Avg Latency</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byModel.map((m: any) => (
                        <tr key={m.modelId}>
                          <td>{m.modelId}</td>
                          <td>{formatNumber(m.callCount)}</td>
                          <td>{formatNumber(m.totalTokens)}</td>
                          <td>{formatCost(m.totalCostMillicents)}</td>
                          <td>{formatLatency(m.avgLatencyMs)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Recent Calls */}
            {recentCalls.length > 0 && (
              <div className="settings-card">
                <h3 className="settings-card__title">Recent Calls</h3>
                <div className="settings-card__content">
                  <table className="settings-table">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Operation</th>
                        <th>Model</th>
                        <th>Tokens</th>
                        <th>Cost</th>
                        <th>Latency</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentCalls.map((c: any) => (
                        <tr key={c.id}>
                          <td>{formatTime(c.createdAt)}</td>
                          <td>{c.operationType}</td>
                          <td>{c.modelId}</td>
                          <td>
                            {c.operationType === 'gptzero'
                              ? `${formatNumber(c.tokensInput)} words`
                              : formatNumber(c.tokensInput + c.tokensOutput)}
                          </td>
                          <td>{formatCost(c.estimatedCostMillicents)}</td>
                          <td>{formatLatency(c.latencyMs)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
